import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Badge,
  useToast,
  Input,
  FormControl,
  FormLabel,
  Spinner,
  IconButton,
  useColorModeValue,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CloseButton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Select,
  Checkbox,
  useBoolean,
  ModalCloseButton,
  Code,
  Tooltip,
  Center
} from '@chakra-ui/react';
import { 
  FaUpload, 
  FaArrowLeft, 
  FaTrash, 
  FaImage, 
  FaCheck, 
  FaTimes, 
  FaUser, 
  FaCamera, 
  FaTag, 
  FaEdit,
  FaMagic,
  FaExternalLinkAlt,
  FaLink,
  FaArrowUp,
  FaArrowDown,
  FaDownload
} from 'react-icons/fa';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';
import { processAndUploadImage } from '../services/removeBgService';
import { addWatermark } from '../utils/imageUtils';
import './UserImagesUpload.css';

const UserImagesUpload = () => {
  const { eventId, userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isTagModalOpen, setIsTagModalOpen] = useBoolean(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useBoolean(false);
  
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [user, setUser] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [authInfo, setAuthInfo] = useState(null);
  const [approvalLink, setApprovalLink] = useState('');
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState({});
  const [isStaff, setIsStaff] = useState(false);
  const [athleteName, setAthleteName] = useState({ first: '', last: '' });
  const [answersData, setAnswersData] = useState(null);
  const [stats, setStats] = useState({
    users: 0,
    events: 0,
    templates: 0,
    uploads: 0
  });
  const [proEdits, setProEdits] = useState([]);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBgColor = useColorModeValue('blue.500', 'blue.700');
  
  // Available tags for images
  const availableTags = [
    { id: 'player', label: 'Player', color: 'blue' },
    { id: 'action', label: 'Action Shot', color: 'orange' },
    { id: 'team', label: 'Team Photo', color: 'green' },
    { id: 'portrait', label: 'Portrait', color: 'purple' },
    { id: 'celebration', label: 'Celebration', color: 'yellow' },
    { id: 'crowd', label: 'Crowd', color: 'pink' },
    { id: 'equipment', label: 'Equipment', color: 'gray' },
    { id: 'venue', label: 'Venue', color: 'teal' }
  ];
  
  // Create a template cache
  const templateCache = new Map();
  
  // Modified loadTemplate function with caching
  const loadTemplate = async (cesdk, templateId) => {
    // Check cache first
    if (templateCache.has(templateId)) {
      console.log(`Loading template ${templateId} from cache`);
      const cachedScene = templateCache.get(templateId);
      await cesdk.engine.scene.loadFromString(cachedScene);
      return true;
    }
    
    // Normal loading logic...
    const sceneText = await fetchSceneText(templateId);
    
    // Cache the scene text
    templateCache.set(templateId, sceneText);
    
    // Load the scene
    await cesdk.engine.scene.loadFromString(sceneText);
    return true;
  };
  
  // Analyze template placeholders once during initialization
  const analyzeTemplate = (template) => {
    // Extract all placeholder metadata
    const placeholderMap = new Map();
    
    // Parse placeholder_data
    const placeholders = JSON.parse(template.placeholder_data || '[]');
    
    // Create a lookup map of block IDs by name
    placeholders.forEach(placeholder => {
      placeholderMap.set(placeholder.name, placeholder.blockId);
    });
    
    return placeholderMap;
  };
  
  // When replacing images, use the direct block ID
  const replaceImage = (cesdk, blockId, imageUrl) => {
    // Direct replacement using known block ID - no searching needed
    const fillId = cesdk.engine.block.getFill(blockId);
    cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", imageUrl);
  };
  
  // Check authentication state
  useEffect(() => {
    // Debug log for authentication status
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Authentication error:', error);
          return;
        }
        
        if (data && data.session) {
          console.log('Authenticated as:', data.session.user.email);
          
          // Check user role using the RPC function
          const { data: userData, error: roleError } = await supabase
            .rpc('get_user_by_id', { user_id: data.session.user.id });
            
          if (roleError) {
            console.error('Error fetching role:', roleError);
          } else {
            console.log('User role:', userData?.user_type || 'unknown');
          }
          
          // Debug: Test direct read from user_uploads
          try {
            const { data: uploadTest, error: uploadError } = await supabase
              .from('user_uploads')
              .select('*', { count: 'exact', head: true });
              
            if (uploadError) {
              console.error('Cannot read from user_uploads:', uploadError);
            } else {
              console.log('Successfully read from user_uploads table, count:', 
                uploadTest && uploadTest.count !== undefined ? uploadTest.count : 'unknown');
            }
          } catch (e) {
            console.error('Error testing user_uploads access:', e);
          }
        } else {
          console.warn('No active session found');
        }
      } catch (e) {
        console.error('Error in auth check:', e);
      }
    };
    
    checkAuth();
  }, []);
  
  // Check if current user is staff or admin
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        console.log("Checking user role...");
        
        const { data: session } = await supabase.auth.getSession();
        if (session && session.session) {
          console.log("Session found, user ID:", session.session.user.id);
          
          const { data: userData, error } = await supabase
            .from('app_users')
            .select('user_type, is_admin')
            .eq('id', session.session.user.id)
            .single();
            
          if (!error && userData) {
            // Debug log the user role information
            console.log("User role data:", userData);
            
            // Check for staff, OR is_admin flag
            const isStaffUser = userData.user_type === 'staff';
            const isAdminUser = userData.is_admin === true;
            
            console.log("User is staff:", isStaffUser);
            console.log("User is admin:", isAdminUser);
            
            // Set isStaff to true if either staff or admin
            setIsStaff(isStaffUser || isAdminUser);
            
            console.log("isStaff state set to:", isStaffUser || isAdminUser);
          } else {
            console.error("Error fetching user role:", error);
            console.error("Or no user data found");
          }
        } else {
          console.log("No active session found");
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };
    
    checkUserRole();
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchEventDetails(),
          fetchUserDetails(),
          fetchUserImages(),
          fetchUserProEdits(),
          fetchAnswers()
        ]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast({
          title: 'Error loading data',
          description: 'Using demo mode',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        
        // Use demo data if fetching fails
        if (!event) {
          setEvent({
            id: eventId,
            name: 'Demo Event',
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            status: 'active',
            city: 'Denver',
            state: 'CO'
          });
        }
        
        if (!user) {
          setUser({
            id: userId,
            first_name: 'Demo',
            last_name: 'User',
            email: 'demo@example.com',
            user_type: 'customer'
          });
        }
        
        if (images.length === 0) {
          // Generate some fake images for demo purposes
          const demoImages = [
            {
              id: 'demo-1',
              user_id: userId,
              event_id: eventId,
              image_url: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=600',
              file_url: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=600',
              status: 'active',
              tags: ['player', 'action'],
              created_at: new Date().toISOString()
            },
            {
              id: 'demo-2',
              user_id: userId,
              event_id: eventId,
              image_url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=600',
              file_url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=600',
              status: 'active',
              tags: ['team'],
              created_at: new Date().toISOString()
            }
          ];
          setImages(demoImages);
        }
        
        setLoading(false);
      }
    };
    
    fetchData();
  }, [eventId, userId]);
  
  const fetchEventDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setEvent(data);
      } else {
        throw new Error('Event not found');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw error; // Propagate the error to be handled by the main useEffect
    }
  };
  
  const fetchUserDetails = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_by_id', { user_id: userId });
      
      if (error) {
        console.error('Error fetching user details:', error);
        console.log('Falling back to mock users data');
      }
      
      if (data) {
        setUser(data);
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };
  
  const fetchUserImages = async () => {
    try {
      // Fix the parameter name
      const uploadType = 'event_upload:' + eventId;
      
      // Before making an RPC call
      console.log('Calling function with params:', { userId, uploadType });
      
      let fetchSuccess = false;
      
      // Add try/catch and log detailed errors
      try {
        // First attempt: Try using RPC get_user_uploads
        const { data, error } = await supabase.rpc('get_user_uploads', { 
          user_id: userId,
          event_upload_type: uploadType
        });
        
        if (error) {
          console.error('RPC Error with get_user_uploads:', error);
          console.error('Parameters:', { userId, uploadType });
        } else {
          console.log('RPC Result - Type:', typeof data, 'Is Array:', Array.isArray(data), 'Length:', data ? (Array.isArray(data) ? data.length : 'not array') : 'no data');
          console.log('RPC Raw Result:', data);
          
          // Process data and set images
          if (data && typeof data === 'object') {
            let processedData = data;
            
            // Convert to array if needed
            if (!Array.isArray(data)) {
              if (typeof data === 'string') {
                try {
                  processedData = JSON.parse(data);
                } catch (e) {
                  console.error('Failed to parse data as JSON:', e);
                  processedData = [];
                }
              } else if (typeof data === 'object') {
                // Try to extract array from object
                if (data[0]) processedData = Object.values(data);
              }
            }
            
            if (processedData && Array.isArray(processedData)) {
              // Map data to expected format
              const formattedData = processedData.map(item => ({
                ...item,
                image_url: item.file_url, // Use file_url as image_url for display
                event_id: eventId // Add the event_id for component compatibility
              }));
              
              setImages(formattedData);
              console.log("Successfully fetched images:", formattedData.length);
              fetchSuccess = true;
            }
          }
        }
      } catch (e) {
        console.error('Exception in get_user_uploads call:', e);
      }
      
      // If first method failed, try direct table query fallback
      if (!fetchSuccess) {
        console.log("Falling back to direct table query");
        try {
          // Try using the fallback function first
          const { data: directData, error: directError } = await supabase.rpc('get_user_uploads_direct', {
            user_id: userId,
            event_upload_type: uploadType
          });
          
          if (directError) {
            console.error("Fallback function also failed:", directError);
            
            // Last resort: direct table query
            const { data: tableData, error: tableError } = await supabase
              .from('user_uploads')
              .select('*')
              .eq('user_id', userId)
              .eq('upload_type', uploadType)
              .eq('status', 'active')
              .order('created_at', { ascending: false });
            
            if (tableError) {
              console.error("All methods failed to fetch uploads:", tableError);
            } else if (tableData) {
              // Map data to expected format
              const formattedData = tableData.map(item => ({
                ...item,
                image_url: item.file_url, // Use file_url as image_url for display
                event_id: eventId // Add the event_id for component compatibility
              }));
              
              setImages(formattedData);
              console.log("Retrieved images using direct table query:", formattedData.length);
            }
          } else if (directData) {
            // Map data to expected format
            const formattedData = directData.map(item => ({
              ...item,
              image_url: item.file_url, // Use file_url as image_url for display
              event_id: eventId // Add the event_id for component compatibility
            }));
            
            setImages(formattedData);
            console.log("Retrieved images using fallback function:", formattedData.length);
          }
        } catch (fallbackError) {
          console.error("Error in fallback method:", fallbackError);
        }
      }
      
      console.log("Successfully read from user_uploads table, count:", images.length);
    } catch (error) {
      console.error('Error fetching user images:', error);
      throw error;
    }
  };
  
  const fetchUserProEdits = async () => {
    try {
      // try join with templates to filter by event and order by priority
      const { data, error } = await supabase
        .from('exports')
        .select('*, templates!inner(id,event_id)')
        .eq('user_id', userId)
        .order('display_priority', { ascending: true, nullsLast: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Join fetch failed, fallback to user-only fetch', error);
        const { data: allExports } = await supabase
          .from('exports')
          .select('*')
          .eq('user_id', userId);
        setProEdits(allExports || []);
        return;
      }

      setProEdits(data || []);
    } catch (e) {
      console.error('Exception fetching pro edits:', e);
    }
  };
  
  const fetchAnswers = async () => {
    try {
      const { data, error } = await supabase
        .from('event_orders')
        .select('answers')
        .eq('user_id', userId)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .single();

      if (error) {
        console.error('answers query error', error);
      }
      const row = data;
      if (row && row.answers) {
        setAnswersData(row.answers);
        // maintain legacy athleteName for other parts if needed
        setAthleteName({
          first: row.answers.athlete_first_name || '',
          last: row.answers.athlete_last_name || ''
        });
      } else {
        console.warn('No answers found in event_orders, trying admin_get_event_users RPC');
        try {
          const { data: rpcData, error: rpcErr } = await supabase.rpc('admin_get_event_users', { event_uuid: eventId });
          if (rpcErr) {
            console.error('admin_get_event_users error', rpcErr);
          } else if (Array.isArray(rpcData)) {
            const found = rpcData.find(u => u.id === userId);
            if (found && found.answers) {
              setAnswersData(found.answers);
              console.log('Answers loaded via RPC');
            }
          }
        } catch (innerErr) {
          console.error('RPC fallback exception', innerErr);
        }
      }
    } catch (err) {
      console.error('Error fetching answers', err);
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Reset progress
    setUploadProgress(0);
    setUploadLoading(true);
    
    // Upload each file
    handleUpload(files);
  };
  
  const handleUpload = async (files) => {
    try {
      const totalFiles = files.length;
      let completedUploads = 0;
      let failedUploads = 0;
      const newImages = [];
      
      // Process images in batches
      const batchSize = 2; // Process 2 images at a time
      const totalImages = files.length;

      for (let batchStart = 0; batchStart < totalImages; batchStart += batchSize) {
        const batch = files.slice(batchStart, batchStart + batchSize);
        const batchPromises = batch.map(file => processImage(file));
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(result => {
          if (result) {
            newImages.push(result);
            completedUploads++;
            setUploadProgress(Math.round((completedUploads / totalFiles) * 100));
          } else {
            failedUploads++;
          }
        });
        
        // Allow UI update between batches
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Update the images state with new uploads
      if (newImages.length > 0) {
        setImages(prev => [...newImages, ...prev]);
        setHasUploaded(true);
        
        toast({
          title: 'Upload successful',
          description: newImages.length + ' image' + (newImages.length !== 1 ? 's' : '') + ' uploaded and processed successfully' + (failedUploads > 0 ? ' (' + failedUploads + ' failed)' : ''),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else if (failedUploads > 0) {
        toast({
          title: 'Upload failed',
          description: 'All ' + failedUploads + ' image' + (failedUploads !== 1 ? 's' : '') + ' failed to process',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const processImage = async (file) => {
    try {
      // Check file type
      if (!file.type.match('image.*')) {
        toast({
          title: 'Invalid file type',
          description: file.name + ' is not an image file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return null;
      }
      
      // Check file size (max 12MB)
      const maxSize = 12 * 1024 * 1024; // 12MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: file.name + ' exceeds the maximum file size of 12MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return null;
      }
      
      // Show processing status
      toast({
        title: 'Processing image',
        description: 'Removing background from ' + file.name + '...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      try {
        // Process and upload the image using client-side remove.bg API with server fallback
        let imageData = null;
        
        try {
          // This now uses a client-side API call with server fallback
          imageData = await processAndUploadImage(file, userId, eventId, selectedTags.length > 0 ? selectedTags : null);
        } catch (bgRemoveError) {
          console.error('Background removal error:', bgRemoveError);
          
          // Tell the user what happened
          toast({
            title: 'Background removal failed',
            description: 'Could not remove background from ' + file.name + '. Uploading original image.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          
          // Upload the original image without background removal
          try {
            toast({
              title: 'Uploading original',
              description: 'Uploading ' + file.name + ' without background removal',
              status: 'info',
              duration: 3000,
              isClosable: true,
            });
            
            // Check authentication status
            const { data: { session }, error: authError } = await supabase.auth.getSession();
            if (authError || !session) {
              console.error('Authentication error:', authError || 'No session');
              throw new Error('Authentication required to upload images');
            }
            
            console.log('Uploading as authenticated user:', session.user.email);
            
            // Generate a unique filename
            const fileExt = file.name.split('.').pop();
            const fileName = userId + '_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10) + '.' + fileExt;
            
            // Upload original file to the originals subfolder
            const originalFilePath = `events/${eventId}/users/${userId}/originals/${fileName}`;
            const { data: originalUploadData, error: originalUploadError } = await supabase.storage
              .from('playmakerpics-images')
              .upload(originalFilePath, file, {
                cacheControl: '3600',
                upsert: false,
                contentType: file.type
              });
            
            if (originalUploadError) {
              throw new Error(`Original image upload failed: ${originalUploadError.message}`);
            }
            
            // Get the public URL for the original file
            const { data: originalUrlData } = supabase.storage
              .from('playmakerpics-images')
              .getPublicUrl(originalFilePath);
            
            const originalImageUrl = originalUrlData.publicUrl;
            
            // Create a watermarked version of the original image
            console.log('Creating watermarked version of the original image...');
            
            // Convert file to blob for watermarking
            const fileReader = new FileReader();
            const fileBlob = await new Promise((resolve) => {
              fileReader.onloadend = () => {
                const dataUrl = fileReader.result;
                const byteString = atob(dataUrl.split(',')[1]);
                const mimeType = dataUrl.split(',')[0].split(':')[1].split(';')[0];
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const intArray = new Uint8Array(arrayBuffer);
                
                for (let i = 0; i < byteString.length; i++) {
                  intArray[i] = byteString.charCodeAt(i);
                }
                
                resolve(new Blob([arrayBuffer], { type: mimeType }));
              };
              fileReader.readAsDataURL(file);
            });
            
            // Apply watermark
            const watermarkedBlob = await addWatermark(fileBlob, {
              text: "PlayMaker Pics",
              color: "rgba(255, 255, 255, 0.8)",
              font: "bold 36px Arial",
              repeating: true,
              diagonal: true,
            });
            
            // Create a file for the watermarked version
            const watermarkedFile = new File(
              [watermarkedBlob],
              `${file.name.split('.')[0]}-watermarked.${fileExt}`,
              { type: file.type }
            );
            
            // Generate a filename for the watermarked version
            const watermarkedFileName = userId + '_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10) + '_wm.' + fileExt;
            // Use specific folder structure with a watermarked subfolder
            const watermarkedFilePath = 'events/' + eventId + '/users/' + userId + '/watermarked/' + watermarkedFileName;
            
            // Upload the watermarked image to Supabase storage
            console.log('Uploading watermarked original image to Supabase storage...');
            const { data: watermarkedUploadData, error: watermarkedUploadError } = await supabase.storage
              .from('playmakerpics-images')
              .upload(watermarkedFilePath, watermarkedFile, {
                cacheControl: '3600',
                upsert: false,
                contentType: file.type
              });
            
            // Get public URL for the watermarked image
            let watermarkedImageUrl = null;
            if (!watermarkedUploadError) {
              const { data: wmPublicUrlData } = supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(watermarkedFilePath);
              
              watermarkedImageUrl = wmPublicUrlData.publicUrl;
              console.log('Watermarked original image URL:', watermarkedImageUrl);
            } else {
              console.error('Error uploading watermarked version:', watermarkedUploadError);
              // Continue without watermark if it fails
            }
            
            // In the processImage function, add this line to define the imageUrl variable
            // after getting the originalImageUrl but before using it
            const imageUrl = originalImageUrl; // Use original image URL since background removal failed

            console.log('Attempting to save upload via process_and_upload function...');
            const { data: dbData, error: dbError } = await supabase
              .from('user_uploads')
              .insert({
                user_id: userId,
                file_url: imageUrl,
                file_url_wm: watermarkedImageUrl,
                source_file_url: originalImageUrl,
                is_primary: false,
                upload_type: 'event_upload:' + eventId,
                file_variant: 'original',
                status: 'active',
                created_at: new Date().toISOString()
              })
              .select()
              .single();
              
            if (dbError) {
              console.error('Upload function error:', dbError);
              throw dbError;
            }
            
            // Format the data for our component
            imageData = {
              id: dbData.id,
              user_id: userId,
              event_id: eventId,
              image_url: imageUrl,
              file_url: imageUrl,
              file_url_wm: watermarkedImageUrl, // Include the watermarked URL
              status: 'active',
              file_variant: 'original',
              tags: selectedTags.length > 0 ? selectedTags : null,
              created_at: dbData.created_at,
              is_primary: dbData.is_primary,
              upload_type: 'event_upload:' + eventId
            };
          } catch (error) {
            console.error('Error uploading original image:', error);
            toast({
              title: 'Upload failed',
              description: 'Error uploading original ' + file.name + ': ' + error.message,
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            return null;
          }
        }
        
        // If still no imageData, fallback to demo mode
        if (!imageData) {
          try {
            // Create a local URL from the original file
            const imageUrl = URL.createObjectURL(file);
            
            // Create a mock image record
            imageData = {
              id: 'local-' + Date.now() + '-' + Math.floor(Math.random() * 10000),
              user_id: userId,
              event_id: eventId, // Keep for component compatibility
              image_url: imageUrl,
              file_url: imageUrl,
              status: 'local',
              file_variant: 'original',
              upload_type: 'event_upload:' + eventId,
              tags: selectedTags.length > 0 ? selectedTags : null,
              created_at: new Date().toISOString()
            };
            
            toast({
              title: 'Demo mode active',
              description: 'Image processed locally. The image is not saved to the database.',
              status: 'info',
              duration: 5000,
              isClosable: true,
            });
            return imageData;
          } catch (processingError) {
            console.error('Processing error:', processingError);
            toast({
              title: 'Processing failed',
              description: 'Could not process ' + file.name + ': ' + processingError.message,
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            return null;
          }
        }
        
        return imageData;
      } catch (error) {
        console.error('Error processing and uploading image:', error);
        toast({
          title: 'Processing failed',
          description: 'Error processing ' + file.name + ': ' + error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return null;
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast({
        title: 'Processing failed',
        description: 'Error processing ' + file.name + ': ' + error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  };
  
  const handleDeleteImage = async (imageId) => {
    try {
      // Find the image to delete
      const imageToDelete = images.find(img => img.id === imageId);
      if (!imageToDelete) {
        toast({
          title: 'Error',
          description: 'Image not found',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // If it's a local image (demo mode), just remove from state
      if (imageToDelete.status === 'local' || imageToDelete.id.startsWith('local-')) {
        // Revoke the blob URLs to prevent memory leaks
        if (imageToDelete.image_url && imageToDelete.image_url.startsWith('blob:')) {
          URL.revokeObjectURL(imageToDelete.image_url);
        }
        
        // Also revoke the watermarked blob URL if it exists
        if (imageToDelete.file_url_wm && imageToDelete.file_url_wm.startsWith('blob:')) {
          URL.revokeObjectURL(imageToDelete.file_url_wm);
        }
        
        // Update local state
        setImages(images.filter(img => img.id !== imageId));
        
        // If we're deleting the currently selected image, close the modal
        if (selectedImage && selectedImage.id === imageId) {
          setSelectedImage(null);
          onClose();
        }
        
        toast({
          title: 'Image deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        return;
      }
      
      // Set to deleted status instead of actually deleting (soft delete)
      const { error: dbError } = await supabase
        .from('user_uploads')
        .update({ status: 'deleted' })
        .eq('id', imageId)
        .eq('user_id', userId); // Extra security to ensure ownership
        
      if (dbError) throw dbError;
      
      // Update local state
      setImages(images.filter(img => img.id !== imageId));
      
      // If we're deleting the currently selected image, close the modal
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage(null);
        onClose();
      }
      
      toast({
        title: 'Image deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const handleTagSelect = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };
  
  const handleImageClick = (image) => {
    setSelectedImage(image);
    onOpen();
  };
  
  const handleEditTags = async (imageId, newTags) => {
    try {
      // Validate inputs
      if (!imageId) {
        throw new Error('Image ID is required');
      }
      
      // Get the image to update
      const imageToUpdate = images.find(img => img.id === imageId);
      if (!imageToUpdate) {
        throw new Error('Image not found');
      }
      
      // Update local state only (no DB update for tags)
      setImages(images.map(img => 
        img.id === imageId ? { ...img, tags: newTags } : img
      ));
      
      if (selectedImage && selectedImage.id === imageId) {
        setSelectedImage({ ...selectedImage, tags: newTags });
      }
      
      toast({
        title: 'Tags updated',
        description: 'Tags updated in memory only',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        title: 'Update failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const openTagEditor = (image) => {
    setSelectedImage(image);
    setEditingTags(image.tags || []);
    setIsTagModalOpen.on();
  };
  
  const handleSaveEditTags = async () => {
    if (selectedImage) {
      await handleEditTags(selectedImage.id, editingTags);
      setIsTagModalOpen.off();
    }
  };
  
  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return '';
    
    // If it's a local image (blob URL)
    if (image.status === 'local' && image.image_url && image.image_url.startsWith('blob:')) {
      return image.image_url;
    }
    
    // If watermarked URL is available, use it for display
    if (image.file_url_wm) {
      return image.file_url_wm;
    }
    
    // For database images
    return image.image_url || image.file_url || '';
  };
  
  // Function to get the original (unwatermarked) image URL
  const getOriginalImageUrl = (image) => {
    if (!image) return '';
    
    // If it's a local image (blob URL)
    if (image.status === 'local' && image.image_url && image.image_url.startsWith('blob:')) {
      return image.image_url;
    }
    
    // First priority: Use source_file_url if available
    if (image.source_file_url) {
      return image.source_file_url;
    }
    
    // Second priority: Try to determine the original file URL based on the file_url pattern
    if (image.file_url) {
      // Check if file_url looks like a processed image path
      const fileUrlPath = image.file_url.split('/');
      
      // Try to reconstruct the original image URL from the file_url pattern
      // by replacing the path segment before the filename with "originals"
      if (fileUrlPath.length > 3) {
        // Get the filename (last segment)
        const fileName = fileUrlPath[fileUrlPath.length - 1];
        
        // Replace the last folder segment with "originals"
        const pathWithoutFilename = fileUrlPath.slice(0, -1);
        
        // If the path is like .../users/{userId}/{filename}
        // Change to .../users/{userId}/originals/{filename}
        pathWithoutFilename[pathWithoutFilename.length - 1] = 'originals';
        
        // Reconstruct the URL
        const reconstructedUrl = [...pathWithoutFilename, fileName].join('/');
        
        // Return reconstructed URL as a fallback
        return reconstructedUrl;
      }
    }
    
    // Fallback: Just return the same image URL if we can't determine the original
    return image.file_url || image.image_url || '';
  };
  
  // Function to generate a secure approval link
  const generateApprovalLink = async () => {
    try {
      setSendingApproval(true);
      
      // Create a unique approval ID
      const approvalId = crypto.randomUUID();
      
      // Store the approval record in the database
      const { data, error } = await supabase
        .from('image_approvals')
        .insert({
          id: approvalId,
          user_id: userId,
          event_id: eventId,
          created_by: (await supabase.auth.getSession()).data.session?.user.id,
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days expiry
        });
      
      if (error) throw error;
      
      // Update status of images to mark them as pending approval
      const imageIds = images.map(img => img.id);
      if (imageIds.length > 0) {
        const { error: updateError } = await supabase
          .from('user_uploads')
          .update({ approval_status: 'pending', approval_id: approvalId })
          .in('id', imageIds);
          
        if (updateError) {
          console.error('Error updating image approval status:', updateError);
        }
      }
      
      // Generate the link
      const approvalUrl = `${window.location.origin}/approve/${approvalId}`;
      setApprovalLink(approvalUrl);
      
      // Show the approval modal
      setIsApprovalModalOpen.on();
      
      // Track approval status locally
      let newApprovalStatus = {};
      images.forEach(img => {
        newApprovalStatus[img.id] = 'pending';
      });
      setApprovalStatus(newApprovalStatus);
      
      // Notify user
      toast({
        title: 'Approval link generated',
        description: 'You can now share this link with the client for image approval',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Send email to user (if available) with approval link
      if (user && user.email) {
        try {
          // You may need to implement or use an email service here
          // For example, using Supabase Edge Functions
          const { error: emailError } = await supabase.functions.invoke('send-approval-email', {
            body: { 
              email: user.email,
              name: user.first_name + ' ' + user.last_name,
              approvalUrl,
              eventName: event?.name || 'Your event',
              imageCount: images.length
            }
          });
          
          if (emailError) {
            console.error('Error sending email:', emailError);
          } else {
            toast({
              title: 'Email sent',
              description: `Approval link has been emailed to ${user.email}`,
              status: 'info',
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
        }
      }
      
    } catch (error) {
      console.error('Error generating approval link:', error);
      toast({
        title: 'Error creating approval link',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingApproval(false);
    }
  };
  
  // Function to copy approval link to clipboard
  const copyApprovalLink = () => {
    navigator.clipboard.writeText(approvalLink);
    toast({
      title: 'Link copied',
      description: 'Approval link copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  // Function to open approval link in a new tab
  const openApprovalLink = () => {
    window.open(approvalLink, '_blank', 'noopener,noreferrer');
  };

  // Function to fetch current approval status
  const fetchApprovalStatus = async () => {
    try {
      // Get the approval status for images in this event for this user
      const { data, error } = await supabase
        .from('user_uploads')
        .select('id, approval_status')
        .eq('user_id', userId)
        .eq('upload_type', 'event_upload:' + eventId);
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        let newApprovalStatus = {};
        data.forEach(item => {
          newApprovalStatus[item.id] = item.approval_status || 'not_sent';
        });
        setApprovalStatus(newApprovalStatus);
      }
    } catch (error) {
      console.error('Error fetching approval status:', error);
    }
  };
  
  // Call fetchApprovalStatus when component loads
  useEffect(() => {
    if (userId && eventId) {
      fetchApprovalStatus();
    }
  }, [userId, eventId]);
  
  // delete pro edit
  const handleDeleteProEdit = async (editId) => {
    try {
      const { error } = await supabase.from('exports').delete().eq('id', editId);
      if (error) throw error;
      setProEdits(proEdits.filter(e => e.id !== editId));
      toast({ title: 'Design deleted', status: 'success', duration: 3000, isClosable: true });
    } catch(err){
      console.error('Delete pro edit error', err);
      toast({ title: 'Delete failed', description: err.message, status: 'error' });
    }
  };
  
  // Function to move a pro edit up or down in the display order
  const handleMoveProEdit = async (editId, direction) => {
    try {
      const index = proEdits.findIndex(e => e.id === editId);
      if (index === -1) return;

      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= proEdits.length) return;

      const current = proEdits[index];
      const swapWith = proEdits[swapIndex];

      // Determine priorities (fallback to array index if not set)
      const currentPriority = current.display_priority ?? index;
      const swapPriority = swapWith.display_priority ?? swapIndex;

      // Update in Supabase (use update to avoid NOT NULL constraints)
      const { error: err1 } = await supabase
        .from('exports')
        .update({ display_priority: swapPriority })
        .eq('id', current.id);

      if (err1) throw err1;

      const { error: err2 } = await supabase
        .from('exports')
        .update({ display_priority: currentPriority })
        .eq('id', swapWith.id);

      if (err2) throw err2;

      // Optimistically update local state
      setProEdits(prev => {
        const arr = [...prev];
        [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
        arr[index].display_priority = swapPriority;
        arr[swapIndex].display_priority = currentPriority;
        return arr;
      });
    } catch (err) {
      console.error('Re-order error', err);
      toast({
        title: 'Re-order failed',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive) setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files && files.length > 0) {
      handleUpload(files);
    }
  };
  
  // Helper to reliably download an image file (works for cross-origin URLs)
  const downloadImage = async (url) => {
    if (!url) return;
    try {
      const fileName = url.split('/').pop()?.split('?')[0] || 'image';

      // Fetch the image data first so we can create an object URL that triggers a download
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download image error', err);
      toast({ title: 'Download failed', description: err.message, status: 'error' });
    }
  };
  
  if (loading) {
    return (
      <Flex justify="center" align="center" height="80vh" width="100%">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text ml={4} fontSize="xl">Loading...</Text>
      </Flex>
    );
  }
  
  // Debug auth info - will show in the UI temporarily
  const authDebug = authInfo ? (
    <Box bg="gray.100" p={2} mb={4} borderRadius="md" fontSize="sm">
      <Text fontWeight="bold">Auth Debug:</Text>
      <Text>Status: {authInfo.authenticated ? 'Authenticated' : 'Not Authenticated'}</Text>
      <Text>User ID: {authInfo.userId}</Text>
      <Text>Role: {authInfo.role}</Text>
      <Text>Email: {authInfo.email}</Text>
    </Box>
  ) : null;
  
  const userObj = Array.isArray(user) ? user[0] : user;
  const usernameDisplay = userObj?.email || userObj?.username || '';
  const athleteFirst = userObj?.answers?.athlete_first_name || athleteName.first;
  const athleteLast = userObj?.answers?.athlete_last_name || athleteName.last;
  const answersObj = answersData || userObj?.answers || null;
  const answerEntries = answersObj
    ? Object.entries(answersObj).filter(([, v]) => v !== null && v !== '')
    : [];
  const formatKey = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  console.log('Header Info:', { usernameDisplay, answersObj, answerValues: answerEntries.map(e=>e[1]) });
  
  return (
    <Box className="user-images-upload-container" p={4}>
      {authDebug}
      <Box mb={6}>
        <Link to={'/dashboard/events/' + eventId}>
          <Button leftIcon={<FaArrowLeft />} variant="ghost" size="sm">
            Back to Event
          </Button>
        </Link>
      </Box>
      
      {user && event && (
        <>
          <Card 
            mb={6} 
            bg={headerBgColor} 
            color="white"
            borderRadius="lg"
            boxShadow="md"
            overflow="hidden"
          >
            <CardBody>
              <Flex justify="space-between" align="center" wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box>
                  <Heading as="h2" size="lg" mb={1}>{event.name}</Heading>
                  <Heading size="md">User Images</Heading>
                  <HStack mt={2}>
                    <FaUser />
                    <Text fontWeight="bold">{userObj?.first_name} {userObj?.last_name}</Text>
                  </HStack>
                  {answerEntries.length > 0 && (
                    <Flex mt={1} align="center" flexWrap="wrap" gap={2}>
                      <Text fontSize="sm" fontWeight="semibold">Custom Data:</Text>
                      {answerEntries.map(([key, val], idx) => (
                        val ? (
                          <React.Fragment key={key}>
                            {idx !== 0 && <Text fontSize="sm">|</Text>}
                            <Tooltip label={formatKey(key)}>
                              <Text fontSize="sm" whiteSpace="nowrap">{String(val)}</Text>
                            </Tooltip>
                          </React.Fragment>
                        ) : null
                      ))}
                    </Flex>
                  )}
                </Box>
              </Flex>
            </CardBody>
          </Card>
          
          {/* Staff only: Approval Button */}
          {/* TEMPORARY OVERRIDE: Removed isStaff check to always show button for testing */}
          {images.length > 0 && (
            <Card mb={6} borderRadius="lg" boxShadow="lg" bg="blue.100">
              <CardBody>
                <Flex direction={{ base: "column", md: "row" }} justifyContent="space-between" alignItems="center">
                  <Box mb={{ base: 4, md: 0 }}>
                    <Heading size="md" color="blue.700">Client Approval</Heading>
                    <Text color="gray.600" fontSize="sm" mt={1}>
                      Send these {images.length} images to the client for review and selection
                    </Text>
                  </Box>
                  
                  <Button
                    leftIcon={<FaCheck />}
                    colorScheme="blue"
                    size="lg"
                    fontSize="md"
                    fontWeight="bold"
                    px={8}
                    py={6}
                    onClick={generateApprovalLink}
                    isLoading={sendingApproval}
                    loadingText="Generating link..."
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                  >
                    Send for Approval
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}
          
          <Card
            mb={6}
            borderRadius="lg"
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            bg={bgColor}
          >
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width="100%"
                    height="auto"
                    minH={{ base: "120px", md: "160px" }}
                    py={6}
                    borderRadius="lg"
                    border="2px dashed"
                    borderColor={isDragActive ? "blue.200" : "blue.50"}
                    bg={isDragActive ? "blue.200" : "blue.50"}
                    _hover={{ bg: "blue.100" }}
                    onDragEnter={handleDragOver}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current.click()}
                    isLoading={uploadLoading}
                    loadingText="Uploading..."
                  >
                    {uploadLoading ? (
                      <VStack>
                        <Text>Processing... {uploadProgress}%</Text>
                        <Box w="100%" h="8px" bg="blue.100" borderRadius="full" overflow="hidden">
                          <Box w={uploadProgress + '%'} h="100%" bg="blue.500" />
                        </Box>
                      </VStack>
                    ) : (
                      <VStack spacing={2} w="100%">
                        <Text className="upload-area-text">Click or drag files here to upload</Text>
                        <Text fontSize="sm" color="gray.500" className="upload-area-subtext">
                          Background will be automatically removed (JPEG, PNG, GIF up to 12MB)
                        </Text>
                      </VStack>
                    )}
                  </Button>
                </FormControl>
                
                {hasUploaded && (
                  <Alert status="success" variant="subtle" borderRadius="md">
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>Processing complete!</AlertTitle>
                      <AlertDescription>
                        Images have been uploaded with background removed.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
          
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Gallery ({images.length})</Tab>
              <Tab>Original Images</Tab>
              <Tab>Pro Edits ({proEdits.length})</Tab>
              {isStaff && <Tab>Approval Status</Tab>}
            </TabList>
            
            <TabPanels>
              <TabPanel>
                {images.length === 0 ? (
                  <Box 
                    textAlign="center" 
                    py={10} 
                    bg={bgColor} 
                    borderRadius="md" 
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <FaImage size={40} style={{ margin: '0 auto', color: 'gray' }} />
                    <Heading size="md" mt={4} color="gray.500">No Images Yet</Heading>
                    <Text mt={2} color="gray.500">
                      Upload images for this user by clicking the upload button above.
                    </Text>
                  </Box>
                ) : (
                  <Grid 
                    templateColumns={{ 
                      base: "repeat(1, 1fr)", 
                      sm: "repeat(2, 1fr)", 
                      md: "repeat(3, 1fr)", 
                      lg: "repeat(4, 1fr)" 
                    }}
                    gap={4}
                  >
                    {images.map(image => (
                      <GridItem 
                        key={image.id} 
                        className="image-card"
                        bg={bgColor}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                        transition="all 0.2s ease"
                        _hover={{ 
                          transform: "translateY(-4px)", 
                          boxShadow: "lg",
                          borderColor: "blue.300" 
                        }}
                      >
                        <Box onClick={() => handleImageClick(image)} cursor="pointer" position="relative">
                          <Box position="relative" pb="100%" overflow="hidden" bg={useColorModeValue('gray.100', 'gray.700')}>
                            <Image
                              src={image.file_url || getImageUrl(image)}
                              alt={'User image ' + image.id}
                              position="absolute"
                              top="0"
                              left="0"
                              width="100%"
                              height="100%"
                              objectFit="contain"
                              fallbackSrc="https://via.placeholder.com/300?text=Loading..."
                            />
                            
                            {/* Approval status badge */}
                            {approvalStatus[image.id] && (
                              <Badge
                                position="absolute"
                                top="8px"
                                right="8px"
                                colorScheme={
                                  approvalStatus[image.id] === 'approved' ? 'green' :
                                  approvalStatus[image.id] === 'rejected' ? 'red' :
                                  approvalStatus[image.id] === 'pending' ? 'yellow' : 'gray'
                                }
                                borderRadius="full"
                                px={2}
                                py={1}
                              >
                                {approvalStatus[image.id] === 'approved' ? 'Approved' :
                                 approvalStatus[image.id] === 'rejected' ? 'Rejected' :
                                 approvalStatus[image.id] === 'pending' ? 'Pending' : 'Not Sent'}
                              </Badge>
                            )}
                          </Box>
                        </Box>
                        
                        <Box p={2}>
                          <HStack wrap="wrap" spacing={1} mb={2}>
                            {image.tags && image.tags.length > 0 ? image.tags.map(tagId => {
                              const tag = availableTags.find(t => t.id === tagId);
                              return tag ? (
                                <Badge 
                                  key={tagId} 
                                  colorScheme={tag.color}
                                  borderRadius="full"
                                  px={2}
                                  py={0.5}
                                  fontSize="xs"
                                  mr={1}
                                  mb={1}
                                >
                                  {tag.label}
                                </Badge>
                              ) : null;
                            }) : (
                              <Text fontSize="xs" color="gray.500">No tags</Text>
                            )}
                          </HStack>
                          
                          <Flex justify="space-between" align="center">
                            <IconButton
                              aria-label="Edit tags"
                              icon={<FaTag />}
                              size="xs"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTagEditor(image);
                              }}
                            />
                            <Text fontSize="xs" color="gray.500">
                              {new Date(image.created_at).toLocaleDateString()}
                            </Text>
                            <IconButton
                              aria-label="Download image"
                              icon={<FaDownload />}
                              size="xs"
                              colorScheme="green"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadImage(image.file_url || getOriginalImageUrl(image));
                              }}
                            />
                            <IconButton
                              aria-label="Delete image"
                              icon={<FaTrash />}
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteImage(image.id);
                              }}
                            />
                          </Flex>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                )}
              </TabPanel>
              
              <TabPanel>
                <Box 
                  p={4}
                  bg={bgColor} 
                  borderRadius="md" 
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Heading size="md" mb={4} color="gray.700">Original Source Images</Heading>
                  
                  {images.length === 0 ? (
                    <Text textAlign="center" color="gray.500">
                      No images have been uploaded yet.
                    </Text>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {images.map(image => {
                        const originalUrl = getOriginalImageUrl(image);
                        return (
                          <Flex 
                            key={image.id} 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="md" 
                            borderColor="gray.200" 
                            bg="white"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Flex alignItems="center">
                              <Box 
                                boxSize="60px" 
                                mr={4} 
                                bg="gray.100" 
                                borderRadius="md" 
                                overflow="hidden"
                              >
                                <Image 
                                  src={originalUrl} 
                                  alt={`Original image ${image.id}`} 
                                  boxSize="60px" 
                                  objectFit="cover" 
                                  fallback={<Center boxSize="60px"><Spinner /></Center>}
                                />
                              </Box>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {new Date(image.created_at).toLocaleString()}
                                </Text>
                                <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="220px">
                                  Source image ID: {image.id}
                                </Text>
                              </VStack>
                            </Flex>
                            <HStack>
                              <Tooltip label="View Original Image">
                                <IconButton
                                  size="sm"
                                  icon={<ExternalLinkIcon />}
                                  aria-label="View Original Image"
                                  onClick={() => window.open(originalUrl, '_blank')}
                                />
                              </Tooltip>
                            </HStack>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </TabPanel>
              
              <TabPanel>
                {proEdits.length === 0 ? (
                  <Box textAlign="center" py={10} bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                    <FaMagic size={40} style={{ margin: '0 auto', color: 'gray' }} />
                    <Heading size="md" mt={4} color="gray.500">No Pro-Edit Designs Yet</Heading>
                    <Text mt={2} color="gray.500">When designs are created for this user they will appear here.</Text>
                  </Box>
                ) : (
                  <Grid templateColumns={{ base: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
                    {proEdits.map(edit => (
                      <GridItem key={edit.id} className="image-card" bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor} overflow="hidden" transition="all 0.2s ease" _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg', borderColor: 'blue.300' }}>
                        <Box cursor="pointer" position="relative" onClick={() => window.open(edit.export_url_wm || edit.export_url,'_blank') }>
                          <Box position="relative" pb="100%" overflow="hidden" bg={useColorModeValue('gray.100', 'gray.700')}>
                            <Image src={edit.export_url_wm || edit.export_url} alt={'Pro Edit'} position="absolute" top="0" left="0" width="100%" height="100%" objectFit="contain" fallbackSrc="https://via.placeholder.com/300?text=Loading..." />
                          </Box>
                        </Box>
                        <Box p={2}>
                          <Flex justify="space-between" align="center">
                            <Text fontSize="xs" color="gray.500">{new Date(edit.created_at).toLocaleDateString()}</Text>
                            <HStack spacing={1}>
                              <IconButton aria-label="Move up" icon={<FaArrowUp />} size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); handleMoveProEdit(edit.id, 'up'); }} />
                              <IconButton aria-label="Move down" icon={<FaArrowDown />} size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); handleMoveProEdit(edit.id, 'down'); }} />
                              <IconButton aria-label="Delete design" icon={<FaTrash />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteProEdit(edit.id); }} />
                            </HStack>
                          </Flex>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                )}
              </TabPanel>
              
              <TabPanel>
                {isStaff ? (
                  <Card borderRadius="md" boxShadow="sm">
                    <CardHeader>
                      <Heading size="md">Image Approval Status</Heading>
                    </CardHeader>
                    <CardBody>
                      {images.length === 0 ? (
                        <Text color="gray.500">No images to approve</Text>
                      ) : (
                        <>
                          <VStack align="stretch" spacing={4}>
                            {images.map(image => (
                              <Flex 
                                key={image.id} 
                                borderWidth="1px" 
                                borderRadius="md" 
                                p={3}
                                align="center"
                                justify="space-between"
                              >
                                <HStack>
                                  <Image 
                                    src={getImageUrl(image)} 
                                    boxSize="50px" 
                                    borderRadius="md"
                                    objectFit="cover"
                                  />
                                  <Text>
                                    {new Date(image.created_at).toLocaleDateString()}
                                  </Text>
                                </HStack>
                                
                                <Badge
                                  colorScheme={
                                    approvalStatus[image.id] === 'approved' ? 'green' :
                                    approvalStatus[image.id] === 'rejected' ? 'red' :
                                    approvalStatus[image.id] === 'pending' ? 'yellow' : 'gray'
                                  }
                                  borderRadius="full"
                                  px={3}
                                  py={1}
                                >
                                  {approvalStatus[image.id] === 'approved' ? 'Approved' :
                                   approvalStatus[image.id] === 'rejected' ? 'Rejected' :
                                   approvalStatus[image.id] === 'pending' ? 'Pending Approval' : 'Not Sent'}
                                </Badge>
                              </Flex>
                            ))}
                          </VStack>
                          
                          {!Object.values(approvalStatus).includes('pending') && 
                           !Object.values(approvalStatus).includes('approved') && (
                            <Button
                              mt={6}
                              colorScheme="blue"
                              leftIcon={<FaCheck />}
                              onClick={generateApprovalLink}
                              isLoading={sendingApproval}
                              width="full"
                            >
                              Send Images for Approval
                            </Button>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                ) : (
                  <Box py={4}>
                    <Text>You don't have permission to access this tab.</Text>
                  </Box>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
          
          {/* Image Detail Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Flex justify="space-between" align="center">
                  <Text>Image Details</Text>
                  <CloseButton onClick={onClose} />
                </Flex>
              </ModalHeader>
              
              <ModalBody>
                {selectedImage && (
                  <VStack spacing={4} align="stretch">
                    <Box borderRadius="md" overflow="hidden" bg={useColorModeValue('gray.100', 'gray.700')}>
                      <Image
                        src={getImageUrl(selectedImage)}
                        alt="Selected image"
                        width="100%"
                        objectFit="contain"
                        fallbackSrc="https://via.placeholder.com/500?text=Loading..."
                      />
                    </Box>
                    
                    <HStack wrap="wrap" spacing={2}>
                      <Text fontWeight="bold">Tags:</Text>
                      {selectedImage.tags && selectedImage.tags.length > 0 ? (
                        selectedImage.tags.map(tagId => {
                          const tag = availableTags.find(t => t.id === tagId);
                          return tag ? (
                            <Badge 
                              key={tagId} 
                              colorScheme={tag.color}
                              borderRadius="full"
                              px={2}
                              py={1}
                            >
                              {tag.label}
                            </Badge>
                          ) : null;
                        })
                      ) : (
                        <Text color="gray.500">No tags assigned</Text>
                      )}
                      
                      <IconButton
                        aria-label="Edit tags"
                        icon={<FaEdit />}
                        size="xs"
                        ml={2}
                        onClick={() => openTagEditor(selectedImage)}
                      />
                    </HStack>
                    
                    <Divider />
                    
                    <Text fontSize="sm" color="gray.500">
                      Uploaded on {new Date(selectedImage.created_at).toLocaleString()}
                    </Text>
                  </VStack>
                )}
              </ModalBody>
              
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={onClose}>
                  Close
                </Button>
                {selectedImage && (
                  <Button 
                    colorScheme="red" 
                    variant="outline"
                    leftIcon={<FaTrash />}
                    onClick={() => {
                      handleDeleteImage(selectedImage.id);
                      onClose();
                    }}
                  >
                    Delete
                  </Button>
                )}
              </ModalFooter>
            </ModalContent>
          </Modal>
          
          {/* Tag Editing Modal */}
          <Modal isOpen={isTagModalOpen} onClose={setIsTagModalOpen.off} size="sm">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Edit Tags</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={3}>
                  {availableTags.map(tag => (
                    <Checkbox 
                      key={tag.id}
                      isChecked={editingTags.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingTags([...editingTags, tag.id]);
                        } else {
                          setEditingTags(editingTags.filter(id => id !== tag.id));
                        }
                      }}
                      colorScheme={tag.color}
                    >
                      <HStack>
                        <Badge colorScheme={tag.color}>{tag.label}</Badge>
                      </HStack>
                    </Checkbox>
                  ))}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={handleSaveEditTags}
                >
                  Save
                </Button>
                <Button variant="ghost" onClick={setIsTagModalOpen.off}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          
          {/* New Approval Link Modal */}
          <Modal isOpen={isApprovalModalOpen} onClose={setIsApprovalModalOpen.off} size="md">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Approval Link Generated</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Success!</AlertTitle>
                      <AlertDescription>
                        Approval link has been generated for {images.length} images
                      </AlertDescription>
                    </Box>
                  </Alert>
                  
                  <Box
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                    fontSize="sm"
                    position="relative"
                  >
                    <Text fontWeight="bold" mb={1}>Share this link with client:</Text>
                    <Text isTruncated>{approvalLink}</Text>
                    <IconButton
                      icon={<FaCheck />}
                      aria-label="Copy link"
                      size="xs"
                      position="absolute"
                      top="8px"
                      right="8px"
                      onClick={copyApprovalLink}
                    />
                  </Box>
                  
                  <Text fontWeight="bold">What happens next?</Text>
                  <Text fontSize="sm">
                    1. Share this link with your client<br />
                    2. They'll be able to view and approve/reject each image<br />
                    3. You'll receive notifications when the client completes the review
                  </Text>
                  
                  <Text fontSize="sm" color="gray.500">
                    This link will expire in 7 days. You can always generate a new one.
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={copyApprovalLink}>
                  Copy Link
                </Button>
                <Button colorScheme="green" mr={3} onClick={openApprovalLink} leftIcon={<FaExternalLinkAlt />}>
                  Open
                </Button>
                <Button variant="ghost" onClick={setIsApprovalModalOpen.off}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
};

export default UserImagesUpload; 